// @mui
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import '../../styles/dashboard.scss';
import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { OrderBox } from 'src/assets/added_icons';
import { useOrderActions, useOrderData } from 'src/context/providers/OrderContext';
import Stack from '@mui/material/Stack';
import OrderDrawerComponent from '../orderPhysical/order-drawer-component';

export const KANBAN_COLUMNS = [
  {
    id: 'WAITING',
    statuses: ['RECEIVED IN TBILISI WAREHOUSE', 'RECEIVED IN US WAREHOUSE', 'READY FOR PICKUP'],
    label: 'Orders',
    info: 'Pickup parcels waiting in the destination warehouse (Tbilisi for USA → GE, New York for GE → USA).',
  },
  { id: 'PICKED UP', label: 'Picked up' },
  { id: 'CHECKED', label: 'Checked' },
  { id: 'DELIVERED', label: 'Delivered'},
];

const SEARCH_MODES = {
  tracking: { label: 'Tracking Number', placeholder: 'Enter tracking number' },
  phone: { label: 'Phone Number', placeholder: 'Enter phone number' },
};

const SEARCH_MODES_INFO = (
  <>
    <strong>Tracking:</strong> Load orders from the same group as the entered tracking number.
    <br />
    <strong>Phone:</strong> Load physical orders where this phone is the primary receiver.
  </>
);

export default function TcTable() {
  const settings = useSettingsContext();
  const {
    getPhysicalOrdersByGroupId,
    getOrdersByReceiverPhone,
    getPhysicalOrderById,
    changePhysicalStatuses,
  } = useOrderActions();
  const orders = useOrderData().physicalCheckoutOrders || [];

  const [searchMode, setSearchMode] = useState('tracking');
  const [searchValue, setSearchValue] = useState('');
  const [scanBarcode, setScanBarcode] = useState('');
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const [aRow, setARow] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [pickingUpOrderId, setPickingUpOrderId] = useState(null);
  const highlightedCardRef = useRef(null);
  const lastLoadParamsRef = useRef({ mode: 'tracking', value: '' });

  useEffect(() => {
    if (!highlightedOrderId) return undefined;
    const el = highlightedCardRef.current;
    if (!el) return undefined;
    const id = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [highlightedOrderId]);

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleModeChange = (_, newMode) => {
    if (newMode != null) {
      setSearchMode(newMode);
      setSearchValue('');
    }
  };

  const refetchPhysicalCheckout = useCallback(
    async (mode, value) => {
      const trimmed = String(value ?? '').trim();
      const params = trimmed
        ? { mode, value: trimmed }
        : lastLoadParamsRef.current;
      const valueToUse = String(params.value ?? '').trim();
      if (!valueToUse) return;
      if (params.mode === 'tracking') {
        await getPhysicalOrdersByGroupId(valueToUse);
      } else {
        await getOrdersByReceiverPhone(valueToUse);
      }
    },
    [getPhysicalOrdersByGroupId, getOrdersByReceiverPhone]
  );

  const handleLoad = () => {
    const value = searchValue.trim();
    if (!value) return;
    lastLoadParamsRef.current = { mode: searchMode, value };
    if (searchMode === 'tracking') {
      getPhysicalOrdersByGroupId(value);
    } else {
      getOrdersByReceiverPhone(value);
    }
  };

  const moveTo = async (e, orderId, status) => {
    e.stopPropagation();
    setPickingUpOrderId(orderId);
    const moved = await changePhysicalStatuses({ order_ids: [orderId], status }).catch(() => null);
    if (moved) await refetchPhysicalCheckout(searchMode, searchValue);
    setPickingUpOrderId(null);
  };

  const handlePickUp = (e, orderId) => moveTo(e, orderId, 'PICKED UP');

  const handleCheck = (e, orderId) => moveTo(e, orderId, 'CHECKED');

  const handleDeliver = (e, orderId) => moveTo(e, orderId, 'DELIVERED');

  const ordersByColumn = KANBAN_COLUMNS.reduce((acc, col) => {
    acc[col.id] = orders.filter((o) => (col.statuses ?? [col.id]).includes(o.status));
    return acc;
  }, {});

  const pickedUpOrders = ordersByColumn['PICKED UP'] || [];

  const handleScanBarcode = () => {
    const trimmed = String(scanBarcode).trim();
    setScanBarcode('');
    if (!trimmed) return;
    const normalized = trimmed.toUpperCase();
    const match = pickedUpOrders.find(
      (o) =>
        String(o?.tracking_number || '').trim().toUpperCase() === normalized ||
        String(o?.tracking_number_2 || '').trim().toUpperCase() === normalized
    );
    if (match) {
      setHighlightedOrderId(match.id);
      toast.success(`Order found: ${match.tracking_number || match.id}`);
    } else {
      setHighlightedOrderId(null);
      toast.warn('No matching order in Picked up column');
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScanBarcode();
    }
  };

  const clickedOrder = (e) => {
    const orderId = e.currentTarget.getAttribute('data-order-id');
    getPhysicalOrderById(orderId).then((data) => {
      if (data) {
        setARow(data);
        setIsDrawerOpen(true);
      }
    });
  };

  return (
    <div>
      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        {Object.keys(aRow)?.length > 0 && (
          <OrderDrawerComponent
            key={aRow.id}
            isDrawerOpen={isDrawerOpen}
            handleDrawerToggle={handleDrawerToggle}
            aRow={aRow}
            setARow={setARow}
          />
        )}

        <div
          style={{
            padding: '4px 0.5% 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <CustomBreadcrumbs heading="Physical check out" sx={{ padding: 0 }} />
        </div>

        <Box
          sx={{
            padding: '4px 0.5% 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'nowrap',
          }}
        >
          <ToggleButtonGroup
            value={searchMode}
            exclusive
            onChange={handleModeChange}
            size="small"
            aria-label="Search by tracking or phone"
          >
            <ToggleButton value="tracking" aria-label="by tracking">
              Tracking
            </ToggleButton>
            <ToggleButton value="phone" aria-label="by phone">
              Phone
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip
            title={SEARCH_MODES_INFO}
            PopperProps={{
              sx: { '& .MuiTooltip-tooltip': { maxWidth: 280 } },
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 16, color: '#2aa76fff', cursor: 'pointer' }}
              aria-label="Info"
            />
          </Tooltip>
          <TextField
            variant="filled"
            size="small"
            sx={{ minWidth: 220 }}
            label={SEARCH_MODES[searchMode].label}
            placeholder={SEARCH_MODES[searchMode].placeholder}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <Button variant="contained" onClick={handleLoad}>
            Load
          </Button>
        </Box>

        <Box
          sx={{
            padding: '4px 0.5% 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'nowrap',
          }}
        >
          <TextField
            variant="filled"
            size="small"
            sx={{ minWidth: 220 }}
            label="Scan barcode (Picked up)"
            placeholder="Scan or enter tracking number"
            value={scanBarcode}
            onChange={(e) => setScanBarcode(e.target.value)}
            onKeyDown={handleScanKeyDown}
          />
          <Button variant="outlined" onClick={handleScanBarcode}>
            Find
          </Button>
        </Box>

        <Box>
          <div
            className="select_containers_large flex_evenly_column"
            style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}
          >
            {KANBAN_COLUMNS.map((col) => (
              <div
                key={col.id}
                className="child_kanban_check"
                style={
                  settings.themeMode === 'light'
                    ? { backgroundColor: '#f4f6f8', padding: '10px', flex: '1', minWidth: '200px' }
                    : { backgroundColor: '#2D353D', padding: '10px', flex: '1', minWidth: '200px' }
                }
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CustomBreadcrumbs heading={col.label} sx={{ color: '#2aa76fff' }} />
                  {col?.info && (
                    <Tooltip title={col.info}>
                      <InfoOutlinedIcon
                        sx={{ fontSize: 16, color: '#2aa76fff', cursor: 'pointer' }}
                        aria-label="Info"
                      />
                    </Tooltip>
                  )}
                </Stack>
                <Box
                  style={
                    settings.themeMode === 'light'
                      ? { backgroundColor: '#f4f5f8', minHeight: '280px', padding: '8px' }
                      : { backgroundColor: '#2d353d', minHeight: '280px', padding: '8px' }
                  }
                >
                  {(ordersByColumn[col.id] || []).map((order) => {
                    const isHighlighted = order.id === highlightedOrderId;
                    const canPickUp = col.id === 'WAITING';
                    const showCheckButton = col.id === 'PICKED UP' && isHighlighted;
                    const showDeliverButton = col.id === 'CHECKED';
                    return (
                      <Box
                        ref={isHighlighted ? highlightedCardRef : null}
                        className="box_items_check"
                        key={order.id}
                        data-order-id={order.id}
                        onClick={clickedOrder}
                        style={
                          settings.themeMode === 'light'
                            ? {
                                backgroundColor: '#ffffff',
                                marginBottom: '8px',
                                ...(isHighlighted && {
                                  outline: '3px solid #2e7d32',
                                  boxShadow: '0 0 0 1px #2e7d32',
                                }),
                              }
                            : {
                                backgroundColor: '#161C24',
                                marginBottom: '8px',
                                ...(isHighlighted && {
                                  outline: '3px solid #2e7d32',
                                  boxShadow: '0 0 0 1px #2e7d32',
                                }),
                              }
                        }
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ width: '100%' }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <img src={OrderBox} width="30px" height="30px" alt="order" />
                            <Stack spacing={0.25}>
                              <span className="bold_heading">
                                {order.tracking_number}{' '}
                                <span
                                  className="light_text"
                                  style={{ fontSize: '0.85em', fontWeight: 500 }}
                                >
                                  ({order.room_number})
                                </span>
                              </span>
                              <span className="light_text">
                                {order?.shelf?.code ? order.shelf.code : ''}
                              </span>
                            </Stack>
                            {canPickUp && (
                              <Tooltip title="Mark as picked up">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={pickingUpOrderId === order.id}
                                  onClick={(e) => handlePickUp(e, order.id)}
                                  aria-label="Pick it up"
                                >
                                  {pickingUpOrderId === order.id ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    <ArrowForwardIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}

                            {showCheckButton && (
                              <Tooltip title="Check and deliver">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={pickingUpOrderId === order.id}
                                  onClick={(e) => handleCheck(e, order.id)}
                                  aria-label="Check and deliver"
                                >
                                  {pickingUpOrderId === order.id ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    <ArrowForwardIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}

                            {showDeliverButton && (
                              <Tooltip title="Mark as delivered">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={pickingUpOrderId === order.id}
                                  onClick={(e) => handleDeliver(e, order.id)}
                                  aria-label="Mark as delivered"
                                >
                                  {pickingUpOrderId === order.id ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    <ArrowForwardIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                  {(!ordersByColumn[col.id] || ordersByColumn[col.id].length === 0) && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: 'center' }}
                    >
                      No orders
                    </Typography>
                  )}
                </Box>
              </div>
            ))}
          </div>
        </Box>
      </Container>
    </div>
  );
}

TcTable.propTypes = {};
